import React, { useEffect, useState } from 'react';
import { Button, TwIconButton } from 'components/common';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  FaTimes, FaInfoCircle, FaUser, FaUsers, FaClock, FaCalendarAlt,
  FaFolderOpen, FaFile, FaShareAlt, FaUserCircle, FaDownload,
  FaHistory, FaLock, FaGlobe,
} from 'react-icons/fa';
import {
  makePublicAsset, removePublicAsset, getVersions, getSharingList,
  removeOwner, downloadAsset,
} from 'redux/drive/driveSlice';
import { getSize } from 'helpers/utils';
import { getFileMeta } from './fileTypes';
const TABS = [
  { id: 'overview', label: 'Overview', icon: FaInfoCircle },
  { id: 'versions', label: 'Versions', icon: FaHistory },
  { id: 'sharing',  label: 'Sharing',  icon: FaShareAlt },
];

const InfoRow = ({ icon: Icon, label, children }) => (
  <div
    style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '12px 0', borderBottom: '1px solid #f1f5f9',
    }}
  >
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      width: 110, flexShrink: 0,
      fontSize: 12, fontWeight: 600, color: '#64748b',
    }}>
      <Icon size={11} style={{ color: '#94a3b8', flexShrink: 0 }} />
      <span>{label}</span>
    </div>
    <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>
      {children || <span style={{ color: '#cbd5e1', fontWeight: 500 }}>—</span>}
    </div>
  </div>
);

InfoRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const Pill = ({ children, color = 'slate', onRemove }) => {
  const palettes = {
    slate:  { bg: '#f1f5f9', text: '#475569', border: '#e2e8f0' },
    blue:   { bg: '#eff6ff', text: '#2563eb', border: '#dbeafe' },
    green:  { bg: '#f0fdf4', text: '#16a34a', border: '#dcfce7' },
    amber:  { bg: '#fffbeb', text: '#d97706', border: '#fef3c7' },
  };
  const p = palettes[color] || palettes.slate;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px',
      backgroundColor: p.bg, color: p.text,
      border: `1px solid ${p.border}`,
      borderRadius: 999,
      fontSize: 11, fontWeight: 700, lineHeight: 1.4,
    }}>
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          style={{
            appearance: 'none', WebkitAppearance: 'none',
            margin: 0, marginLeft: 2, padding: 0,
            width: 14, height: 14,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: 'transparent', border: 'none',
            color: p.text, cursor: 'pointer', fontSize: 12, lineHeight: 1,
            opacity: 0.6,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.6'; }}
        >
          ×
        </button>
      )}
    </span>
  );
};

Pill.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['slate', 'blue', 'green', 'amber']),
  onRemove: PropTypes.func,
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: 10, marginTop: 16,
  }}>
    <Icon size={10} style={{ color: '#94a3b8' }} />
    <span>{children}</span>
  </div>
);

SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const DriveDetailDrawer = ({ show, onClose, asset }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('overview');
  const [versions, setVersions] = useState([]);
  const [sharings, setSharings] = useState({});
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [loadingSharings, setLoadingSharings] = useState(false);

  const isFolder = asset?.asset_type === 'folder';
  const isPublic = asset?.public;
  const { icon: Icon, color } = asset ? getFileMeta(asset) : { icon: FaFile, color: '#64748b' };

  const { allow_make_public, allow_remove_public, allow_download, allow_remove_owner } = asset?.permissions || {};

  /* lock body scroll + Escape */
  useEffect(() => {
    if (!show) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [show, onClose]);

  /* reset state on open */
  useEffect(() => {
    if (show && asset) {
      setActiveTab('overview');
      fetchVersions();
      fetchSharings();
    }
    // reload only when opened or the asset id changes; asset object refreshes (e.g. make public) must not reset the tab and refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, asset?.id]);

  const fetchVersions = async () => {
    if (!asset?.id) return;
    setLoadingVersions(true);
    try {
      const data = await dispatch(getVersions(asset.id)).unwrap();
      setVersions(Array.isArray(data) ? data.map((i) => i.attributes) : []);
    } catch {
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const fetchSharings = async () => {
    if (!asset?.id) return;
    setLoadingSharings(true);
    try {
      const data = await dispatch(getSharingList(asset.id)).unwrap();
      setSharings(data || {});
    } catch {
      setSharings({});
    } finally {
      setLoadingSharings(false);
    }
  };

  const handleTogglePublic = () => {
    try {
      dispatch(isPublic ? removePublicAsset(asset.id) : makePublicAsset(asset.id));
    } catch (e) { toast.error(e.message); }
  };

  const handleDownload = () => {
    if (!asset?.id) return;
    dispatch(downloadAsset({ assetId: asset.id, fileName: asset.name }));
  };

  const handleRemoveOwner = (userName) => {
    try {
      dispatch(removeOwner({ assetId: asset.id, userId: userName })).unwrap()
        .then(() => onClose());
    } catch (e) { toast.error(e.message); }
  };

  if (!show || !asset) return null;

  const ownerNames = sharings.owners?.map((o) => o.attributes.user_name) || [];
  const userNames  = sharings.users?.map((u) => u.attributes.user_name) || [];
  const deptNames  = sharings.departments?.map((d) => d.attributes.department_name) || [];

  const drawer = (
    <div
      style={{
        position: 'fixed', inset: 0,
        zIndex: 999999,
        display: 'flex', justifyContent: 'flex-end',
        backgroundColor: 'rgba(15,23,42,0.4)',
        animation: 'driveDrawerFadeIn 180ms ease',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes driveDrawerFadeIn { from { background-color: rgba(15,23,42,0); } to { background-color: rgba(15,23,42,0.4); } }
        @keyframes driveDrawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480, height: '100%',
          backgroundColor: '#ffffff',
          display: 'flex', flexDirection: 'column',
          boxShadow: '-12px 0 32px rgba(15,23,42,0.12)',
          animation: 'driveDrawerSlideIn 220ms cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0, padding: '14px 16px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, flexShrink: 0,
            backgroundColor: '#f8fafc', border: '1px solid #f1f5f9',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={20} style={{ color }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{
              margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              lineHeight: 1.4,
            }} title={asset.name}>
              {asset.name}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {isFolder ? 'Folder' : (asset.content_type || 'File')}
            </p>
          </div>
          <TwIconButton icon={FaTimes} size="md" variant="ghost" onClick={onClose} title="Close" />
        </div>

        {/* Access banner */}
        <div style={{
          flexShrink: 0,
          margin: '14px 16px 0',
          padding: '10px 12px',
          backgroundColor: isPublic ? '#f0fdf4' : '#f8fafc',
          border: `1px solid ${isPublic ? '#dcfce7' : '#e2e8f0'}`,
          borderRadius: 10,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: isPublic ? '#dcfce7' : '#f1f5f9',
            borderRadius: 8,
            color: isPublic ? '#16a34a' : '#64748b',
          }}>
            {isPublic ? <FaGlobe size={12} /> : <FaLock size={12} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
              {isPublic ? 'Public' : 'Private'}
            </p>
            <p style={{ margin: '1px 0 0', fontSize: 11, color: '#64748b', fontWeight: 500 }}>
              {isPublic ? 'Anyone in the organization can access' : 'Only authorised users can access'}
            </p>
          </div>
          {(allow_make_public || allow_remove_public) && (
            <Button
              variant={isPublic ? 'secondary' : 'primary'}
              size="xs"
              leftIcon={isPublic ? FaLock : FaGlobe}
              onClick={handleTogglePublic}
            >
              {isPublic ? 'Make private' : 'Make public'}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div style={{
          flexShrink: 0,
          padding: '14px 16px 0',
          display: 'flex', gap: 4,
          borderBottom: '1px solid #e2e8f0',
        }}>
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            const TIcon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                style={{
                  appearance: 'none', WebkitAppearance: 'none', boxShadow: 'none',
                  margin: 0, padding: '8px 12px',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  backgroundColor: 'transparent', border: 'none',
                  borderBottom: `2px solid ${isActive ? '#0f172a' : 'transparent'}`,
                  marginBottom: -1,
                  color: isActive ? '#0f172a' : '#64748b',
                  fontSize: 12, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1.4,
                  cursor: 'pointer',
                  transition: 'color 120ms ease, border-color 120ms ease',
                }}
              >
                <TIcon size={11} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 16px 16px' }}>
          {activeTab === 'overview' && (
            <div>
              <InfoRow icon={FaFile} label="Name">
                <span style={{ wordBreak: 'break-word' }}>{asset.name}</span>
              </InfoRow>
              <InfoRow icon={FaFolderOpen} label="Location">
                {asset.parent_id ? (asset.parent_name || 'Folder') : 'My Drive'}
              </InfoRow>
              <InfoRow icon={FaUser} label="Owner">
                {asset.created_by}
              </InfoRow>
              <InfoRow icon={FaClock} label="Modified">
                {asset.updated_date}
              </InfoRow>
              <InfoRow icon={FaCalendarAlt} label="Created">
                {asset.created_date}
              </InfoRow>
              {!isFolder && (
                <InfoRow icon={FaInfoCircle} label="Size">
                  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                    {getSize(asset.file_size || 0)}
                  </span>
                </InfoRow>
              )}
              {asset.content_type && !isFolder && (
                <InfoRow icon={FaInfoCircle} label="Type">
                  <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 11 }}>
                    {asset.content_type}
                  </span>
                </InfoRow>
              )}
            </div>
          )}

          {activeTab === 'versions' && (
            <div>
              {loadingVersions ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                  Loading versions...
                </div>
              ) : versions.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center' }}>
                  <FaHistory size={28} style={{ color: '#cbd5e1', marginBottom: 8 }} />
                  <p style={{ margin: 0, fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                    No version history available
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
                  {versions.map((v, idx) => {
                    const isCurrent = (idx + 1) === versions.length;
                    return (
                      <div
                        key={idx}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '10px 12px',
                          backgroundColor: isCurrent ? '#eff6ff' : '#f8fafc',
                          border: `1px solid ${isCurrent ? '#dbeafe' : '#e2e8f0'}`,
                          borderRadius: 10,
                        }}
                      >
                        <div style={{
                          width: 28, height: 28, flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: isCurrent ? '#dbeafe' : '#e2e8f0',
                          color: isCurrent ? '#2563eb' : '#64748b',
                          borderRadius: 8,
                          fontSize: 11, fontWeight: 700,
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        }}>
                          v{idx + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.4 }}>
                            Version {idx + 1}
                            {isCurrent && (
                              <span style={{ marginLeft: 8 }}>
                                <Pill color="blue">Current</Pill>
                              </span>
                            )}
                          </p>
                          <p style={{ margin: '1px 0 0', fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                            {v.created_date}
                          </p>
                        </div>
                        {allow_download && (
                          <TwIconButton icon={FaDownload} size="sm" variant="secondary" onClick={handleDownload} title="Download" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sharing' && (
            <div>
              {loadingSharings ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>
                  Loading sharing info...
                </div>
              ) : (
                <>
                  <SectionTitle icon={FaUser}>Owners</SectionTitle>
                  {ownerNames.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {ownerNames.map((nm) => (
                        <Pill key={nm} color="blue" onRemove={allow_remove_owner ? () => handleRemoveOwner(nm) : undefined}>
                          <FaUserCircle size={10} /> {nm}
                        </Pill>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>No owners</p>
                  )}

                  <SectionTitle icon={FaUsers}>Shared with Employees</SectionTitle>
                  {userNames.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {userNames.map((nm) => (
                        <Pill key={nm} color="slate">
                          <FaUserCircle size={10} /> {nm}
                        </Pill>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Not shared with any employees</p>
                  )}

                  <SectionTitle icon={FaUsers}>Shared with Departments</SectionTitle>
                  {deptNames.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {deptNames.map((nm) => (
                        <Pill key={nm} color="green">
                          <FaUsers size={10} /> {nm}
                        </Pill>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Not shared with any departments</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          flexShrink: 0, padding: '12px 16px',
          borderTop: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose}>Close</Button>
          {allow_download && !isFolder && (
            <Button variant="primary" size="sm" leftIcon={FaDownload} onClick={handleDownload}>
              Download
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
};

DriveDetailDrawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
};

export default DriveDetailDrawer;
